'use client';

import { c as _c } from "react/compiler-runtime";
import { domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { UserIdentity } from '@/components/profile/user-identity';
import { ReactionVoteButtons } from '@/components/shared/reaction-vote-buttons';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Tick01IconSolid } from '@/components/ui/icon/glyphs';
import { type MarketOutcomeColorClasses, useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { SuggestionWire } from './types';
import { effectiveImageUrl, effectiveLabel, effectiveTicker } from './types';
import { useSuggestionVoteModel } from './use-suggestion-vote-model';
interface SuggestionFeedRowProps {
  suggestion: SuggestionWire;
  isCreator?: boolean;
  isPromoted: boolean;
  reservedOutcomeIndex?: number;
  showSeparator?: boolean;
  onAddToMarket?: (() => void) | undefined;
}
export function SuggestionFeedRow(t0) {
  const $ = _c(67);
  const {
    suggestion,
    isCreator: t1,
    isPromoted,
    reservedOutcomeIndex,
    showSeparator: t2,
    onAddToMarket
  } = t0;
  const isCreator = t1 === undefined ? false : t1;
  const showSeparator = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== suggestion) {
    t3 = effectiveLabel(suggestion);
    $[0] = suggestion;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const label = t3;
  let t4;
  if ($[2] !== suggestion) {
    t4 = effectiveTicker(suggestion);
    $[2] = suggestion;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const ticker = t4;
  let t5;
  if ($[4] !== suggestion) {
    t5 = effectiveImageUrl(suggestion);
    $[4] = suggestion;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const imageUrl = t5;
  let t6;
  if ($[6] !== suggestion.suggesterPubkey) {
    t6 = suggestion.suggesterPubkey.slice(0, 4);
    $[6] = suggestion.suggesterPubkey;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== suggestion.suggesterPubkey) {
    t7 = suggestion.suggesterPubkey.slice(-4);
    $[8] = suggestion.suggesterPubkey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const truncatedPubkey = `${t6}…${t7}`;
  let t8;
  if ($[10] !== suggestion.suggester.connectedProviders) {
    t8 = suggestion.suggester.connectedProviders ?? [];
    $[10] = suggestion.suggester.connectedProviders;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const connectedProviders = t8;
  const hasReason = suggestion.reason !== null && suggestion.reason.length > 0;
  const prefersReducedMotion = useReducedMotion();
  const identityColor = useSuggestionIdentityColor(suggestion, reservedOutcomeIndex);
  const t9 = `suggestion-row-${suggestion.id}`;
  let t10;
  if ($[12] !== prefersReducedMotion) {
    t10 = prefersReducedMotion === true ? {
      duration: 0
    } : MOTION_POSITION;
    $[12] = prefersReducedMotion;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = isPromoted ? "true" : "false";
  let t12;
  if ($[14] !== identityColor || $[15] !== isPromoted || $[16] !== showSeparator) {
    t12 = suggestionRowClassName(isPromoted, showSeparator, identityColor);
    $[14] = identityColor;
    $[15] = isPromoted;
    $[16] = showSeparator;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== imageUrl || $[19] !== label) {
    t13 = <OptimizedImage variant="thumbnail" src={imageUrl} alt={label} size="size-9" sizes="36px" rounded="rounded-item" className="block size-9 shrink-0 self-start" imageClassName="size-9 object-cover" />;
    $[18] = imageUrl;
    $[19] = label;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== label) {
    t14 = <span className="truncate text-body font-medium text-foreground-primary">{label}</span>;
    $[21] = label;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== ticker) {
    t15 = <span className="shrink-0 font-mono text-caption text-foreground-secondary">${ticker}</span>;
    $[23] = ticker;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14 || $[26] !== t15) {
    t16 = <div className="flex items-baseline gap-2">{t14}{t15}</div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== connectedProviders || $[29] !== suggestion.suggester) {
    t17 = {
      ...suggestion.suggester,
      connectedProviders
    };
    $[28] = connectedProviders;
    $[29] = suggestion.suggester;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17 || $[32] !== truncatedPubkey) {
    t18 = <UserIdentity user={t17} variant="inline" size="sm" fallback={truncatedPubkey} />;
    $[31] = t17;
    $[32] = truncatedPubkey;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span aria-hidden="true" className="shrink-0">·</span>;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== suggestion.createdAt) {
    t20 = relativeTime(suggestion.createdAt);
    $[35] = suggestion.createdAt;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t20) {
    t21 = <span className="shrink-0">{t20}</span>;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t18 || $[40] !== t21) {
    t22 = <div className="flex min-w-0 items-center gap-1.5 text-caption text-foreground-tertiary">{t18}{t19}{t21}</div>;
    $[39] = t18;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== hasReason || $[43] !== suggestion.reason) {
    t23 = hasReason ? <p className="line-clamp-2 break-words text-caption text-foreground-secondary">{suggestion.reason}</p> : null;
    $[42] = hasReason;
    $[43] = suggestion.reason;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t16 || $[46] !== t22 || $[47] !== t23) {
    t24 = <div className="flex min-w-0 flex-col gap-1 self-start">{t16}{t22}{t23}</div>;
    $[45] = t16;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = isPromoted ? "self-center" : "self-start";
  let t26;
  if ($[49] !== t25) {
    t26 = cn("flex items-center gap-2", t25);
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== isCreator || $[52] !== isPromoted || $[53] !== onAddToMarket || $[54] !== suggestion) {
    t27 = isPromoted ? <CreatorStatus status={suggestion.status} /> : <ActiveRowActions suggestion={suggestion} isCreator={isCreator} onAddToMarket={onAddToMarket} />;
    $[51] = isCreator;
    $[52] = isPromoted;
    $[53] = onAddToMarket;
    $[54] = suggestion;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t26 || $[57] !== t27) {
    t28 = <div className={t26}>{t27}</div>;
    $[56] = t26;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t10 || $[60] !== t11 || $[61] !== t12 || $[62] !== t13 || $[63] !== t24 || $[64] !== t28 || $[65] !== t9) {
    t29 = <LazyMotion features={domMax}><m.div layout={true} layoutId={t9} transition={t10} data-promoted={t11} className={t12}>{t13}{t24}{t28}</m.div></LazyMotion>;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t24;
    $[64] = t28;
    $[65] = t9;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  return t29;
}
function suggestionRowClassName(isPromoted: boolean, showSeparator: boolean, identityColor: MarketOutcomeColorClasses | null): string {
  return cn('grid grid-cols-[36px_1fr_auto] items-start gap-3 py-3', !isPromoted && showSeparator && 'border-t border-border-secondary', isPromoted && 'rounded-panel-row border px-3', isPromoted && identityColor === null && 'border-border-secondary bg-surface-secondary', isPromoted && identityColor?.outline, isPromoted && identityColor !== null && `border-outcome-${identityColor.hue}-fill`);
}
function useSuggestionIdentityColor(suggestion, reservedOutcomeIndex) {
  const $ = _c(7);
  const outcomeIndex = suggestion.status === "promoting" ? reservedOutcomeIndex ?? null : suggestion.outcomeIndex;
  let t0;
  if ($[0] !== outcomeIndex) {
    t0 = Number.isInteger(outcomeIndex) && outcomeIndex !== null && outcomeIndex >= 0 && outcomeIndex < 10;
    $[0] = outcomeIndex;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasValidOutcomeIndex = t0;
  const canonicalColor = useMarketOutcomeColor(hasValidOutcomeIndex ? suggestion.marketPubkey : null, outcomeIndex ?? -1);
  let t1;
  let t2;
  if ($[2] !== hasValidOutcomeIndex || $[3] !== suggestion.id || $[4] !== suggestion.status) {
    t1 = () => {
      if (suggestion.status !== "promoted" || hasValidOutcomeIndex) {
        return;
      }
      logger.warn("suggested_outcomes.promoted_index_missing", {
        suggestionId: suggestion.id,
        status: suggestion.status
      });
    };
    t2 = [hasValidOutcomeIndex, suggestion.id, suggestion.status];
    $[2] = hasValidOutcomeIndex;
    $[3] = suggestion.id;
    $[4] = suggestion.status;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  return canonicalColor.hue === undefined ? null : canonicalColor;
}
function CreatorStatus(t0) {
  const $ = _c(8);
  const {
    status
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes");
  let t1;
  if ($[0] !== status) {
    t1 = status === "promoted" ? <Icon icon={Tick01IconSolid} className="size-3.5" /> : null;
    $[0] = status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== status || $[3] !== t) {
    t2 = status === "promoted" ? t("addedByCreator") : t("creator.adding");
    $[2] = status;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className="inline-flex items-center gap-1.5 rounded-pill border border-feedback-success-border bg-feedback-success-surface px-2.5 py-1 text-caption font-medium text-feedback-success-content">{t1}{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ActiveRowActions(t0) {
  const $ = _c(15);
  const {
    suggestion,
    isCreator,
    onAddToMarket
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      disabled: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const voteModel = useSuggestionVoteModel(suggestion.id, suggestion, t1);
  let t2;
  if ($[1] !== voteModel.downAriaLabel || $[2] !== voteModel.downCount || $[3] !== voteModel.onReact || $[4] !== voteModel.reaction || $[5] !== voteModel.upAriaLabel || $[6] !== voteModel.upCount) {
    t2 = <ReactionVoteButtons reaction={voteModel.reaction} upCount={voteModel.upCount} downCount={voteModel.downCount} upAriaLabel={voteModel.upAriaLabel} downAriaLabel={voteModel.downAriaLabel} onReact={voteModel.onReact} size="sm" />;
    $[1] = voteModel.downAriaLabel;
    $[2] = voteModel.downCount;
    $[3] = voteModel.onReact;
    $[4] = voteModel.reaction;
    $[5] = voteModel.upAriaLabel;
    $[6] = voteModel.upCount;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isCreator || $[9] !== onAddToMarket || $[10] !== t) {
    t3 = isCreator && onAddToMarket !== undefined ? <AnimatedButton type="button" variant="primary" size="sm" onClick={onAddToMarket}>{t("addToMarket")}</AnimatedButton> : null;
    $[8] = isCreator;
    $[9] = onAddToMarket;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}